<script setup>
// [PROSPEK] 2026-04-05 Add watch, computed, timeStampAppendedURL for delayed attachment reactivity
import { ref, computed, watch } from 'vue';
// [/PROSPEK]
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';
// [PROSPEK] 2026-04-05 Cache-bust delayed attachment URLs
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
// [/PROSPEK]

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

// [PROSPEK] 2026-04-05 Cache-busted URL computed and resetState for delayed attachment reactivity
const mediaUrl = computed(() => {
  return attachment.value?.dataUrl
    ? timeStampAppendedURL(attachment.value.dataUrl)
    : '';
});

const { isLoaded, hasError, loadWithRetry, resetState } = useLoadWithRetry();
// [/PROSPEK]

const showGallery = ref(false);
const isDownloading = ref(false);

// [PROSPEK] 2026-04-05 Retry image loading when delayed message.updated replaces the attachment object
watch(
  attachment,
  currentAttachment => {
    if (!currentAttachment?.dataUrl) {
      resetState();
      return;
    }

    loadWithRetry(mediaUrl.value);
  },
  { immediate: true }
);
// [/PROSPEK]

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <!-- [PROSPEK] 2026-04-05 Loading spinner and cache-busted mediaUrl for delayed attachments -->
    <div
      v-else-if="!isLoaded"
      class="flex justify-center items-center rounded-lg min-h-24"
    >
      <Icon icon="i-lucide-loader-circle" class="size-5 animate-spin" />
    </div>
    <!-- [/PROSPEK] -->
    <div v-else class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu"
        :src="mediaUrl"
        :width="attachment.width"
        :height="attachment.height"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
